
import { useState } from "react";
import { toast } from "sonner";
import { useFundImageMutation } from "./useFundImageMutation";
import { 
  IMAGE_PATHS, 
  FILE_NAMES,
  validateImageFile,
  validateImageUrl,
  uploadStorageFile,
  uploadFromUrl,
  StorageError
} from '@/utils/image-storage';
import { logger } from "@/services/LoggingService";
import { supabase } from "@/integrations/supabase/client";
import { STORAGE_BUCKET } from "@/utils/image-storage/constants";
import { getObjectPathFromPublicUrl } from "@/utils/image-storage/remote";

const COMPONENT = 'ImageManagement';

interface UseImageManagementProps {
  fundId: string;
  onImageUpdate: (newImageUrl: string | null) => void;
}


export const useImageManagement = ({ fundId, onImageUpdate }: UseImageManagementProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: updateImage } = useFundImageMutation(fundId);

  const handleFileUpload = async (file: File): Promise<boolean> => {
    if (!file || !fundId) {
      toast.error("No file selected");
      return false;
    }

    try {
      logger.info(COMPONENT, 'Starting file upload', {
        fundId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      setIsUploading(true);
      validateImageFile(file);
      
      const publicUrl = await uploadStorageFile(
        IMAGE_PATHS.FUND(fundId),
        FILE_NAMES.FUND_LOGO,
        file,
        { upsert: true }
      );
      
      // Optimize to WebP and prefer it as canonical
      const path = getObjectPathFromPublicUrl(publicUrl)?.split('/')?.slice(1).join('/') || '';
      let finalUrl = publicUrl;
      if (path) {
        const { data, error } = await supabase.functions.invoke('optimize-image', {
          body: { bucket: STORAGE_BUCKET, path, quality: 80 }
        });
        if (!error && data?.webpUrl) {
          finalUrl = data.webpUrl as string;
        }
      }
      
      await updateImage(finalUrl);
      onImageUpdate(finalUrl);
      
      logger.info(COMPONENT, 'File upload successful', {
        fundId,
        publicUrl: finalUrl
      });

      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'File upload failed', {
        fundId,
        error: error.message,
        details: error instanceof StorageError ? error.details : error
      });
      
      toast.error(error.message || "Failed to upload image");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUrlUpdate = async (imageUrl: string): Promise<boolean> => {
    if (!imageUrl.trim()) {
      toast.error("Please enter a valid image URL");
      return false;
    }

    try {
      logger.info(COMPONENT, 'Starting URL upload', {
        fundId,
        imageUrl
      });

      setIsUploading(true);
      validateImageUrl(imageUrl);
      
      const publicUrl = await uploadFromUrl(
        IMAGE_PATHS.FUND(fundId),
        FILE_NAMES.FUND_LOGO,
        imageUrl,
        { upsert: true }
      );

      // Optimize to WebP and prefer it as canonical
      const path = getObjectPathFromPublicUrl(publicUrl)?.split('/')?.slice(1).join('/') || '';
      let finalUrl = publicUrl;
      if (path) {
        const { data, error } = await supabase.functions.invoke('optimize-image', {
          body: { bucket: STORAGE_BUCKET, path, quality: 80 }
        });
        if (!error && data?.webpUrl) {
          finalUrl = data.webpUrl as string;
        }
      }

      await updateImage(finalUrl);
      onImageUpdate(finalUrl);

      logger.info(COMPONENT, 'URL upload successful', {
        fundId,
        publicUrl: finalUrl
      });

      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'URL upload failed', {
        fundId,
        imageUrl,
        error: error.message,
        details: error instanceof StorageError ? error.details : error
      });
      
      toast.error(error.message || "Failed to update image URL");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageRemove = async (): Promise<boolean> => {
    try {
      logger.info(COMPONENT, 'Starting image removal', { fundId });
      
      setIsUploading(true);
      await updateImage(null);
      onImageUpdate(null);

      logger.info(COMPONENT, 'Image removal successful', { fundId });
      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'Image removal failed', {
        fundId,
        error: error.message,
        details: error instanceof StorageError ? error.details : error
      });
      
      toast.error(error.message || "Failed to remove image");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    handleFileUpload,
    handleImageUrlUpdate,
    handleImageRemove
  };
};
